import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useState } from "react";
import { useStaffForm } from "../../hooks/staff/use-staff-form";
import { useUpdateStaff } from "@/hooks/staff/use-staff";
import type { Staff } from "@/types/staff/api-types/staff-types";
import { getStaffFormValues } from "@/default-form-values/staff/default-staff-form-values";
import {
  STAFF_DIALOG_WIDTH,
  STAFF_DIALOG_HEIGHT,
} from "@/constants/staff/staff-constants";
import { Separator } from "@/components/ui/separator";
import { StaffInfoStep } from "./add-staff-steps/staff-info-step";
import { StaffHoursStep } from "./add-staff-steps/staff-hours-step";
import { StaffServicesStep } from "./add-staff-steps/staff-services-step";
import { StaffDialogProgress } from "./add-staff-steps/staff-dialog-progress";
import { staffFormSchema } from "@/zod-schemas/staff/add-staff-schema";
import { toStaffRequest } from "@/mappers/staff/staff-form-to-request";

interface EditStaffDialogProps {
  staff: Staff;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditStaffDialog({
  staff,
  open,
  onOpenChange,
}: EditStaffDialogProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);

  const updateStaffMutation = useUpdateStaff();

  const form = useStaffForm({
    defaultValues: getStaffFormValues(staff),
    validators: {
      onChange: staffFormSchema,
      onSubmit: staffFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const updatedStaff = await updateStaffMutation.mutateAsync({
          id: staff.id,
          data: toStaffRequest(value),
        });

        toast.success(
          `Staff member "${updatedStaff.first_name} ${updatedStaff.last_name}" updated successfully`,
        );
        setCurrentStep(0);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        const errorDetail = error?.response?.data?.detail || "";

        if (statusCode === 409) {
          setServerError(errorDetail);
          setCurrentStep(0);
        } else {
          toast.error(
            `Failed to update staff member "${value.first_name} ${value.last_name}"`,
          );
          setCurrentStep(0);
          onOpenChange(false);
        }
      }
    },
  });

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setCurrentStep(0);
      setServerError(null);
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`${STAFF_DIALOG_WIDTH} ${STAFF_DIALOG_HEIGHT} overflow-y-auto`}
      >
        <DialogHeader>
          <DialogTitle>Edit Staff</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Separator />
          <StaffDialogProgress currentStep={currentStep} />

          <div className="mt-6">
            {currentStep === 0 && (
              <StaffInfoStep
                form={form}
                onNext={handleNext}
                onCancel={() => handleOpenChange(false)}
                serverError={serverError}
                setServerError={setServerError}
              />
            )}
            {currentStep === 1 && (
              <StaffHoursStep
                form={form}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {currentStep === 2 && (
              <StaffServicesStep form={form} onBack={handleBack} />
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
